import React, { useEffect, useState } from "react";
import { db } from "../../Firebase/FirebaseConnection";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  onSnapshot,
  deleteDoc,
  addDoc,
} from "firebase/firestore";
import {
  Table,
  Button,
  Tag,
  message,
  Modal,
  Descriptions,
  DatePicker,
  Row,
  Col,
  Statistic,
  Tooltip,
  Input,
  Popconfirm,
  Form,
} from "antd";
import { DeleteOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";
import "./ReadingSession.css";
import moment from "moment";

const { RangePicker } = DatePicker;
const { Search } = Input;

const ReadingSession = () => {
  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [searchTerm, setSearchTerm] = useState("");
  const uniqueStudents = new Set(
    filteredSessions.map((session) => session.studentNumber)
  );
  const studentCount = uniqueStudents.size;
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [editForm, setEditForm] = useState({
    fullName: "",
    studentNumber: "",
    bookTitle: "",
    yearLevel: "",
    department: "",
    section: "",
  });
  const [saving, setSaving] = useState(false);
  const userName = localStorage.getItem("userName");
  const logUserActivity = async ({
    actionType,
    actionDescription,
    oldStatus,
    newStatus,
    bookTitle,
    updatedBy,
    changes = null,
    studentName = null,
  }) => {
    try {
      const timestamp = new Date();
      const formattedDateTime = moment(timestamp).format(
        "MMMM DD, YYYY [at] hh:mm:ss A"
      );

      await addDoc(collection(db, "user-activity-logs"), {
        actionType,
        actionDescription,
        oldStatus,
        newStatus,
        bookTitle,
        timestamp,
        formattedDateTime,
        updatedBy,
        changes,
        studentName,
      });
    } catch (error) {
      console.error("Error logging activity:", error);
    }
  };

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "reading_sessions"),
      (snapshot) => {
        const sessionData = snapshot.docs
          .map((doc) => ({
            key: doc.id,
            id: doc.id,
            ...doc.data(),
          }))
          .sort((a, b) => {
            const timeA = a.timestamp?.toDate?.().getTime?.() || 0;
            const timeB = b.timestamp?.toDate?.().getTime?.() || 0;
            return timeB - timeA;
          });

        setSessions(sessionData);
        setFilteredSessions(sessionData);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const handleDateFilter = (dates) => {
    setDateRange(dates);

    if (dates && dates.length === 2) {
      const [start, end] = dates;
      const filtered = sessions.filter((session) => {
        if (session.timestamp) {
          const sessionDate = session.timestamp.toDate();
          return sessionDate >= start.toDate() && sessionDate <= end.toDate();
        }
        return false;
      });

      setFilteredSessions(
        filtered.sort((a, b) => {
          const timeA = a.timestamp?.toDate?.().getTime?.() || 0;
          const timeB = b.timestamp?.toDate?.().getTime?.() || 0;
          return timeA - timeA;
        })
      );
    } else {
      setFilteredSessions(sessions);
    }
  };

  const readingCount = filteredSessions.filter(
    (session) => session.status === "Reading"
  ).length;

  const doneReadingCount = filteredSessions.filter(
    (session) => session.status === "Done Reading"
  ).length;

  const updateStatus = async (id, newStatus) => {
    try {
      const session = sessions.find((s) => s.id === id);
      const oldStatus = session.status;

      const sessionRef = doc(db, "reading_sessions", id);
      const updateData = { status: newStatus };
      if (newStatus === "Done Reading") {
        updateData.finishedTimestamp = new Date();
      }

      await updateDoc(sessionRef, updateData);

      message.success(`Status updated to "${newStatus}"`);

      await logUserActivity({
        actionType: "statusUpdate",
        actionDescription: `Book status updated from "${oldStatus}" to "${newStatus}"`,
        oldStatus,
        newStatus,
        bookTitle: session.bookTitle,
        updatedBy: userName,
      });
    } catch (error) {
      console.error("Error updating status:", error);
      message.error("Failed to update status");
    }
  };

  const showDetails = (session) => {
    setSelectedSession(session);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedSession(null);
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);

    const filtered = sessions.filter(
      (session) =>
        session.fullName.toLowerCase().includes(value) ||
        session.studentNumber.toLowerCase().includes(value)
    );

    setFilteredSessions(
      filtered.sort((a, b) => {
        const timeA = a.timestamp?.toDate?.().getTime?.() || 0;
        const timeB = b.timestamp?.toDate?.().getTime?.() || 0;
        return timeA - timeA;
      })
    );
  };

  const deleteSession = async (id) => {
    const sessionToDelete = sessions.find((s) => s.id === id);
    try {
      await deleteDoc(doc(db, "reading_sessions", id));
      message.success("Session deleted successfully!");
    } catch (error) {
      message.error("Failed to delete session!");
      await logUserActivity({
        actionType: "deleteSession",
        actionDescription: `Deleted session for "${sessionToDelete.bookTitle}"`,
        oldStatus: sessionToDelete.status,
        newStatus: null,
        bookTitle: sessionToDelete.bookTitle,
        updatedBy: userName,
      });
      console.error(error);
    }
  };

  const openEditModal = (session) => {
    setEditingSession(session);
    setEditForm({
      fullName: session.fullName,
      studentNumber: session.studentNumber,
      bookTitle: session.bookTitle,
      yearLevel: session.yearLevel,
      department: session.department,
      section: session.section,
      status: session.status,
    });
    setIsEditModalOpen(true);
  };

  const handleEditChange = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  const saveChanges = async () => {
    if (!editingSession) return;
    setSaving(true);
    try {
      const sessionRef = doc(db, "reading_sessions", editingSession.id);
      await updateDoc(sessionRef, {
        ...editForm,
        status: editingSession.status,
        timestamp: editingSession.timestamp || new Date(),
        finishedTimestamp: editingSession.finishedTimestamp || null,
      });

      message.success("Student details updated successfully!");
      const changes = {};
      Object.keys(editForm).forEach((key) => {
        if (editForm[key] !== editingSession[key]) {
          changes[key] = {
            old: editingSession[key] || "",
            new: editForm[key] || "",
          };
        }
      });

      await logUserActivity({
        actionType: "editSession",
        actionDescription: `Edited session details for "${editingSession.bookTitle}"`,
        studentName: editingSession.fullName,
        oldStatus: editingSession.status,
        newStatus: editingSession.status,
        bookTitle: editingSession.bookTitle,
        updatedBy: userName,
        changes,
      });

      setIsEditModalOpen(false);
      setEditingSession(null);
    } catch (error) {
      console.error("Error updating student:", error);
      message.error("Failed to update details");
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: "Student",
      dataIndex: "fullName",
      key: "fullName",
      width: 200,
    },
    {
      title: "Book Title",
      dataIndex: "bookTitle",
      key: "bookTitle",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: "Borrowed", value: "Reading" },
        { text: "Returned", value: "Done Reading" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => {
        const displayStatus =
          status === "Done Reading"
            ? "Returned"
            : status === "Reading"
            ? "Borrowed"
            : status;

        return (
          <Tag color={status === "Done Reading" ? "green" : "blue"}>
            {displayStatus}
          </Tag>
        );
      },
    },

    {
      title: "Actions",
      key: "actions",
      width: 430,
      render: (_, record) => (
        <>
          <Button
            type="primary"
            onClick={() => updateStatus(record.id, "Reading")}
            disabled={record.status === "Reading"}
          >
            Mark as Borrowed
          </Button>
          <Button
            type="default"
            onClick={() => updateStatus(record.id, "Done Reading")}
            disabled={record.status === "Done Reading"}
            style={{ marginLeft: "8px" }}
          >
            Mark as Returned
          </Button>
          <Button
            type="link"
            icon={<EyeOutlined className="action-icon" />}
            onClick={() => showDetails(record)}
          />
          <Button
            type="link"
            onClick={() => openEditModal(record)}
            icon={<EditOutlined className="action-icon" />}
          ></Button>
          <Popconfirm
            title="Are you sure to delete this session?"
            onConfirm={() => deleteSession(record.id)}
            okText="Yes"
            cancelText="No"
            placement="left"
          >
            <Button
              type="link"
              icon={<DeleteOutlined className="action-icon" />}
            />
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <div className="tab-main-container">
      <div style={{ textAlign: "justify", marginBottom: "10px" }}>
        <div>
          <Row justify="start" style={{ marginBottom: "20px" }}>
            <Col xs={24} sm={12} md={10}>
              <Tooltip
                title="Select a date range to filter reading sessions"
                placement="top"
                color="#996c6c"
              >
                <RangePicker
                  format="YYYY-MM-DD"
                  onChange={handleDateFilter}
                  value={dateRange}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "8px",
                    border: "1px solid #ccc",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                  }}
                />
              </Tooltip>
            </Col>

            <div className="search-container student-search">
              <Search
                className="custom-search"
                allowClear
                type="text"
                placeholder="Search by full name or student number..."
                value={searchTerm}
                onChange={handleSearch}
                enterButton="Search"
                size="large"
              />
            </div>
          </Row>
        </div>

        <Row gutter={[16, 16]} justify="start">
          <Col xs={12} sm={6} md={4}>
            <div className="statistic-container statistic-student">
              <Statistic title="🧑🏻‍🎓👨🏻‍🎓 Total Students" value={studentCount} />
            </div>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <div className="statistic-container statistic-reading">
              <Statistic title="📖 Borrowed" value={readingCount} />
            </div>
          </Col>

          <Col xs={12} sm={6} md={4}>
            <div className="statistic-container statistic-done">
              <Statistic title="✅ Returned" value={doneReadingCount} />
            </div>
          </Col>
        </Row>
      </div>

      <div className="table-container">
        <Table
          className="custom-table"
          columns={columns}
          dataSource={filteredSessions}
          loading={loading}
          pagination={{ pageSize: 4 }}
          scroll={{ x: "max-content" }}
        />
      </div>

      <Modal
        className="custom-modal"
        title="Reading Session Details"
        visible={isModalOpen}
        onCancel={closeModal}
        footer={null}
      >
        {selectedSession && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="Student">
              {selectedSession.fullName}
            </Descriptions.Item>
            <Descriptions.Item label="Student Number">
              {selectedSession.studentNumber}
            </Descriptions.Item>
            <Descriptions.Item label="Book Title">
              {selectedSession.bookTitle}
            </Descriptions.Item>
            <Descriptions.Item label="Year Level">
              {selectedSession.yearLevel}
            </Descriptions.Item>
            <Descriptions.Item label="Department">
              {selectedSession.department}
            </Descriptions.Item>
            <Descriptions.Item label="Section">
              {selectedSession.section}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag
                color={
                  selectedSession.status === "Done Reading" ? "green" : "blue"
                }
              >
                {selectedSession.status === "Done Reading"
                  ? "Returned"
                  : selectedSession.status === "Reading"
                  ? "Borrowed"
                  : selectedSession.status}
              </Tag>
            </Descriptions.Item>

            <Descriptions.Item label="Started">
              {selectedSession.timestamp?.toDate().toLocaleString()}
            </Descriptions.Item>
            {selectedSession.finishedTimestamp && (
              <Descriptions.Item label="Finished">
                {selectedSession.finishedTimestamp?.toDate().toLocaleString()}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>

      <Modal
        className="custom-modal"
        title="Edit Student Details"
        visible={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        onOk={saveChanges}
        footer={[
          <Button key="cancel" onClick={() => setIsEditModalOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={saveChanges}
            loading={saving}
            disabled={saving}
          >
            Save
          </Button>,
        ]}
      >
        <Form layout="vertical">
          <Form.Item label="Full Name">
            <Input
              name="fullName"
              value={editForm.fullName}
              onChange={handleEditChange}
              placeholder="Enter full name"
            />
          </Form.Item>

          <Form.Item label="Student Number">
            <Input
              name="studentNumber"
              value={editForm.studentNumber}
              onChange={handleEditChange}
              placeholder="Enter student number"
            />
          </Form.Item>

          <Form.Item label="Book Title">
            <Input
              name="bookTitle"
              value={editForm.bookTitle}
              onChange={handleEditChange}
              placeholder="Enter book title"
            />
          </Form.Item>

          <Form.Item label="Year Level">
            <Input
              name="yearLevel"
              value={editForm.yearLevel}
              onChange={handleEditChange}
              placeholder="Enter year level"
            />
          </Form.Item>

          <Form.Item label="Department">
            <Input
              name="department"
              value={editForm.department}
              onChange={handleEditChange}
              placeholder="Enter department"
            />
          </Form.Item>

          <Form.Item label="Section">
            <Input
              name="section"
              value={editForm.section}
              onChange={handleEditChange}
              placeholder="Enter section"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ReadingSession;
